import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProjectService } from '@app/services/project.service';
import { SlideTypeLinesService } from '@app/services/slide-type-lines.service';
import { ToastrService } from 'ngx-toastr';

export interface CustomerRow {
  CustomerShortCode: string;
  CustomerName: string;
  totalLicenses?: number;
  licenseMode?: string;
  licensesUsed?: number;
  licensesAvailable?: number;
  pptBackgroundFile?: string | null;
  pptLogoFile?: string | null;
  pptLayoutName?: string | null;
  pptLayoutWidthCm?: number | null;
  pptLayoutHeightCm?: number | null;
  pptSlideMasterName?: string | null;
  slideTypeCount?: number;
}

export interface PptLayoutEdit {
  pptLayoutName: string;
  pptLayoutWidthCm: number | null;
  pptLayoutHeightCm: number | null;
  pptSlideMasterName: string;
}

export interface SlideTypeOption {
  slideTypeShortcode: string;
  slideTypeName?: string;
}

type CustomerSortField = 'CustomerShortCode' | 'CustomerName';

@Component({
  selector: 'app-manage-customers',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-manage-customers.component.html',
})
export class ManageCustomersComponent implements OnInit {
  customerList: CustomerRow[] = [];
  filteredCustomers: CustomerRow[] = [];
  error = '';
  loading = true;
  searchText = '';
  sortField: CustomerSortField = 'CustomerShortCode';
  sortAsc = true;

  newCustomer: CustomerRow = {
    CustomerShortCode: '',
    CustomerName: '',
  };

  licenseEdits: Record<string, { totalLicenses: number; licenseMode: 'POOL' | 'ASSIGNED' }> = {};
  savingLicense: Record<string, boolean> = {};
  uploadingAsset: Record<string, boolean> = {};

  allSlideTypes: SlideTypeOption[] = [];
  slideTypeEdits: Record<string, string[]> = {};
  slideTypesInUse: Record<string, string[]> = {};
  savingSlideTypes: Record<string, boolean> = {};
  pptLayoutEdits: Record<string, PptLayoutEdit> = {};
  savingPptLayout: Record<string, boolean> = {};

  constructor(
    private projectService: ProjectService,
    private slideTypeLinesService: SlideTypeLinesService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.loadAllSlideTypes();
    this.loadCustomers();
  }

  private loadAllSlideTypes(): void {
    this.slideTypeLinesService.getslideTypeList().subscribe({
      next: (rows: SlideTypeOption[]) => {
        this.allSlideTypes = Array.isArray(rows)
          ? rows
              .map((r) => ({
                slideTypeShortcode: String(r.slideTypeShortcode || '').trim(),
                slideTypeName: r.slideTypeName,
              }))
              .filter((r) => r.slideTypeShortcode)
              .sort((a, b) => a.slideTypeShortcode.localeCompare(b.slideTypeShortcode))
          : [];
      },
      error: () => {
        this.toastr.error('Failed to load slide types');
      },
    });
  }

  loadCustomers(): void {
    this.loading = true;
    this.projectService.getCustomers().subscribe({
      next: (res: CustomerRow[]) => {
        this.customerList = Array.isArray(res) ? res : [];
        for (const c of this.customerList) {
          const code = c.CustomerShortCode;
          if (!this.licenseEdits[code]) {
            this.licenseEdits[code] = {
              totalLicenses: Number(c.totalLicenses) || 0,
              licenseMode: (String(c.licenseMode || 'POOL').toUpperCase() === 'ASSIGNED' ? 'ASSIGNED' : 'POOL'),
            };
          }
        }
        this.refreshUsageCounts();
        this.applyFilterAndSort();
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load customers.';
        this.toastr.error('Failed to load customers');
        this.loading = false;
      },
    });
  }

  private refreshUsageCounts(): void {
    for (const c of this.customerList) {
      this.projectService.getCustomerLicenseSummary(c.CustomerShortCode).subscribe({
        next: (summary: any) => {
          if (!summary) return;
          c.licensesUsed = summary.licensesUsed;
          c.licensesAvailable = summary.licensesAvailable;
          c.totalLicenses = summary.totalLicenses;
          c.licenseMode = summary.licenseMode;
          c.pptBackgroundFile = summary.pptBackgroundFile;
          c.pptLogoFile = summary.pptLogoFile;
          const linked = Array.isArray(summary.slideTypes) ? summary.slideTypes : [];
          c.slideTypeCount = linked.length;
          this.slideTypeEdits[c.CustomerShortCode] = linked
            .map((t: { slideTypeShortcode?: string }) => String(t.slideTypeShortcode || '').trim())
            .filter(Boolean);
          this.slideTypesInUse[c.CustomerShortCode] = Array.isArray(summary.slideTypesInUse)
            ? summary.slideTypesInUse.map((sc: string) => String(sc || '').trim()).filter(Boolean)
            : [];
          this.pptLayoutEdits[c.CustomerShortCode] = {
            pptLayoutName: String(summary.pptLayoutName || ''),
            pptLayoutWidthCm:
              summary.pptLayoutWidthCm != null && summary.pptLayoutWidthCm !== ''
                ? Number(summary.pptLayoutWidthCm)
                : null,
            pptLayoutHeightCm:
              summary.pptLayoutHeightCm != null && summary.pptLayoutHeightCm !== ''
                ? Number(summary.pptLayoutHeightCm)
                : null,
            pptSlideMasterName: String(summary.pptSlideMasterName || ''),
          };
          this.licenseEdits[c.CustomerShortCode] = {
            totalLicenses: Number(summary.totalLicenses) || 0,
            licenseMode: summary.licenseMode === 'ASSIGNED' ? 'ASSIGNED' : 'POOL',
          };
        },
      });
    }
  }

  addCustomer(): void {
    const code = String(this.newCustomer.CustomerShortCode || '').trim();
    const name = String(this.newCustomer.CustomerName || '').trim();
    if (!code || !name) {
      this.toastr.warning('Customer short code and name are required');
      return;
    }

    this.projectService.addCustomer({ CustomerShortCode: code, CustomerName: name }).subscribe({
      next: () => {
        this.toastr.success('Customer added successfully');
        this.newCustomer = { CustomerShortCode: '', CustomerName: '' };
        this.loadCustomers();
      },
      error: (err) => {
        const msg = err?.error?.Data?.message || err?.error?.message || 'Failed to add customer';
        this.toastr.error(msg);
      },
    });
  }

  saveLicenses(customer: CustomerRow): void {
    const code = customer.CustomerShortCode;
    const edit = this.licenseEdits[code];
    if (!edit) return;
    this.savingLicense[code] = true;
    this.projectService
      .setCustomerLicenses({
        CustomerShortCode: code,
        totalLicenses: Number(edit.totalLicenses) || 0,
        licenseMode: edit.licenseMode,
      })
      .subscribe({
        next: (summary: any) => {
          customer.totalLicenses = summary?.totalLicenses;
          customer.licenseMode = summary?.licenseMode;
          customer.licensesUsed = summary?.licensesUsed;
          customer.licensesAvailable = summary?.licensesAvailable;
          this.toastr.success('Licenses updated');
          this.savingLicense[code] = false;
        },
        error: (err) => {
          this.toastr.error(err?.error?.Data?.message || 'Failed to update licenses');
          this.savingLicense[code] = false;
        },
      });
  }

  saveSlideTypes(customer: CustomerRow): void {
    const code = customer.CustomerShortCode;
    const codes = [...(this.slideTypeEdits[code] || [])];
    this.savingSlideTypes[code] = true;
    this.projectService
      .setCustomerSlideTypes({
        CustomerShortCode: code,
        slideTypeShortcodes: codes,
      })
      .subscribe({
        next: (summary: any) => {
          const linked = Array.isArray(summary?.slideTypes) ? summary.slideTypes : [];
          customer.slideTypeCount = linked.length;
          this.slideTypeEdits[code] = linked
            .map((t: { slideTypeShortcode?: string }) => String(t.slideTypeShortcode || '').trim())
            .filter(Boolean);
          this.slideTypesInUse[code] = Array.isArray(summary?.slideTypesInUse)
            ? summary.slideTypesInUse.map((sc: string) => String(sc || '').trim()).filter(Boolean)
            : [];
          this.toastr.success('Slide types updated');
          this.savingSlideTypes[code] = false;
        },
        error: (err) => {
          const msg = err?.error?.Data?.message || 'Failed to update slide types';
          this.toastr.error(msg);
          this.savingSlideTypes[code] = false;
          this.projectService.getCustomerLicenseSummary(code).subscribe({
            next: (summary: any) => {
              if (!summary) return;
              const linked = Array.isArray(summary.slideTypes) ? summary.slideTypes : [];
              this.slideTypeEdits[code] = linked
                .map((t: { slideTypeShortcode?: string }) => String(t.slideTypeShortcode || '').trim())
                .filter(Boolean);
              this.slideTypesInUse[code] = Array.isArray(summary.slideTypesInUse)
                ? summary.slideTypesInUse.map((sc: string) => String(sc || '').trim()).filter(Boolean)
                : [];
            },
          });
        },
      });
  }

  isSlideTypeSelected(customerCode: string, slideTypeShortcode: string): boolean {
    return (this.slideTypeEdits[customerCode] || []).includes(slideTypeShortcode);
  }

  isSlideTypeInUse(customerCode: string, slideTypeShortcode: string): boolean {
    return (this.slideTypesInUse[customerCode] || []).includes(slideTypeShortcode);
  }

  isSlideTypeRemovalLocked(customerCode: string, slideTypeShortcode: string): boolean {
    return (
      this.isSlideTypeSelected(customerCode, slideTypeShortcode) &&
      this.isSlideTypeInUse(customerCode, slideTypeShortcode)
    );
  }

  toggleSlideType(customerCode: string, slideTypeShortcode: string, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const checked = input.checked;
    if (!checked && this.isSlideTypeInUse(customerCode, slideTypeShortcode)) {
      input.checked = true;
      this.toastr.warning(
        'This slide type is used on project slides for this customer and cannot be removed.',
      );
      return;
    }
    let list = [...(this.slideTypeEdits[customerCode] || [])];
    if (checked) {
      if (!list.includes(slideTypeShortcode)) list.push(slideTypeShortcode);
    } else {
      list = list.filter((sc) => sc !== slideTypeShortcode);
    }
    this.slideTypeEdits[customerCode] = list;
  }

  savePptLayout(customer: CustomerRow): void {
    const code = customer.CustomerShortCode;
    const edit = this.pptLayoutEdits[code];
    if (!edit) return;
    this.savingPptLayout[code] = true;
    this.projectService
      .setCustomerPptLayout({
        CustomerShortCode: code,
        pptLayoutName: String(edit.pptLayoutName || '').trim() || null,
        pptLayoutWidthCm: edit.pptLayoutWidthCm,
        pptLayoutHeightCm: edit.pptLayoutHeightCm,
        pptSlideMasterName: String(edit.pptSlideMasterName || '').trim() || null,
      })
      .subscribe({
        next: (summary: any) => {
          this.pptLayoutEdits[code] = {
            pptLayoutName: String(summary?.pptLayoutName || ''),
            pptLayoutWidthCm:
              summary?.pptLayoutWidthCm != null ? Number(summary.pptLayoutWidthCm) : null,
            pptLayoutHeightCm:
              summary?.pptLayoutHeightCm != null ? Number(summary.pptLayoutHeightCm) : null,
            pptSlideMasterName: String(summary?.pptSlideMasterName || ''),
          };
          this.toastr.success('PPT layout updated');
          this.savingPptLayout[code] = false;
        },
        error: (err) => {
          this.toastr.error(err?.error?.Data?.message || 'Failed to update PPT layout');
          this.savingPptLayout[code] = false;
        },
      });
  }

  onBrandingFileSelected(customer: CustomerRow, assetType: 'background' | 'logo', ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const key = `${customer.CustomerShortCode}_${assetType}`;
    this.uploadingAsset[key] = true;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      this.projectService
        .uploadCustomerBrandingAsset({
          CustomerShortCode: customer.CustomerShortCode,
          assetType,
          fileName: file.name,
          fileBase64: dataUrl,
        })
        .subscribe({
          next: (summary: any) => {
            customer.pptBackgroundFile = summary?.pptBackgroundFile;
            customer.pptLogoFile = summary?.pptLogoFile;
            this.toastr.success(`${assetType} uploaded`);
            this.uploadingAsset[key] = false;
            input.value = '';
          },
          error: (err) => {
            this.toastr.error(err?.error?.Data?.message || 'Upload failed');
            this.uploadingAsset[key] = false;
            input.value = '';
          },
        });
    };
    reader.readAsDataURL(file);
  }

  isUploading(customerCode: string, assetType: string): boolean {
    return !!this.uploadingAsset[`${customerCode}_${assetType}`];
  }

  onSearchChange(): void {
    this.applyFilterAndSort();
  }

  setSort(field: CustomerSortField): void {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = true;
    }
    this.applyFilterAndSort();
  }

  sortIndicator(field: CustomerSortField): string {
    if (this.sortField !== field) return '';
    return this.sortAsc ? ' ▲' : ' ▼';
  }

  private applyFilterAndSort(): void {
    const q = this.searchText.trim().toLowerCase();
    let rows = [...this.customerList];
    if (q) {
      rows = rows.filter(
        (c) =>
          String(c.CustomerShortCode || '').toLowerCase().includes(q) ||
          String(c.CustomerName || '').toLowerCase().includes(q),
      );
    }
    const field = this.sortField;
    const dir = this.sortAsc ? 1 : -1;
    rows.sort((a, b) => {
      const av = String(a[field] || '').toLowerCase();
      const bv = String(b[field] || '').toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    this.filteredCustomers = rows;
  }
}
